import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { HL7, SYSTEMS } from '@/consts/systems.consts';
import {
  ConditionInputItem,
  mapConditionsToInputItems,
} from '@/utils/condition.utils';

export interface UseConditionsReturn {
  loadPatientConditions: (patient: Patient) => Promise<{
    conditions: ConditionInputItem[];
  }>;

  loadEncounterConditions: (
    patient: Patient,
    encounter: Encounter
  ) => Promise<{
    conditions: ConditionInputItem[];
  }>;
}

export function useConditions(): UseConditionsReturn {
  const medplum = useMedplum();

  const loadPatientConditions = useCallback(
    async (
      patient: Patient
    ): Promise<{
      conditions: ConditionInputItem[];
    }> => {
      if (!patient?.id) {
        return {
          conditions: [],
        };
      }

      // Load medical history conditions
      const conditions = await medplum.searchResources('Condition', {
        subject: `Patient/${patient.id}`,
        category: `${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE}|${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.MEDICAL_HISTORY}`,
      });

      // Map to ConditionInputItem format (handles encounter diagnosis and problem list item relationships)
      const conditionInputs = mapConditionsToInputItems(conditions);

      return {
        conditions: conditionInputs,
      };
    },
    [medplum]
  );

  const loadEncounterConditions = useCallback(
    async (
      patient: Patient,
      encounter: Encounter
    ): Promise<{
      conditions: ConditionInputItem[];
    }> => {
      if (!patient?.id || !encounter?.id) {
        return {
          conditions: [],
        };
      }

      // Load all conditions for this encounter
      const allConditions = await medplum.searchResources('Condition', {
        subject: `Patient/${patient.id}`,
        encounter: `Encounter/${encounter.id}`,
        'code:not': `${SYSTEMS.HPO}|`,
        'category:not': `${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.GENETIC_DISEASE}|,${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.BASE}|${HL7.TERMINOLOGY.CS.CONDITION.CATEGORY.CODES.MEDICAL_HISTORY}`,
      });
      // Map to ConditionInputItem format
      const conditionInputs = mapConditionsToInputItems(allConditions);

      return {
        conditions: conditionInputs,
      };
    },
    [medplum]
  );

  return { loadPatientConditions, loadEncounterConditions };
}
